import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../api/client'

const STATUS_BADGE: Record<string, string> = { active: 'badge-success', inactive: 'badge-neutral', trial: 'badge-warning' }

const EMPTY_FORM = { name: '', slug: '', email: '', adminEmail: '', adminPassword: '', adminFirstName: '', adminLastName: '', plan: 'standard', phone: '', taxId: '', address: '' }

export default function Tenants () {
  const navigate = useNavigate()
  const [tenants, setTenants] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [showModal, setShowModal] = useState(false)
  const [form, setForm] = useState(EMPTY_FORM)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  function load () {
    api.get('/tenants').then(res => setTenants(res.data.data)).finally(() => setLoading(false))
  }
  useEffect(load, [])

  function set (field: string, value: string) {
    setForm(p => ({ ...p, [field]: value }))
    // Auto-generar slug desde el nombre
    if (field === 'name') {
      setForm(p => ({ ...p, name: value, slug: value.toLowerCase().trim().replace(/[^a-z0-9]/g, '-').replace(/-+/g, '-').replace(/^-|-$/g, '') }))
    }
  }

  async function handleCreate () {
    setError('')
    if (!form.name || !form.email || !form.adminEmail || !form.adminPassword || !form.adminFirstName) {
      setError('Completa todos los campos obligatorios')
      return
    }
    setSaving(true)
    try {
      await api.post('/auth/register', {
        companyName: form.name,
        slug: form.slug,
        companyEmail: form.email,
        companyPhone: form.phone,
        companyTaxId: form.taxId,
        companyAddress: form.address,
        plan: form.plan,
        email: form.adminEmail,
        password: form.adminPassword,
        firstName: form.adminFirstName,
        lastName: form.adminLastName
      })
      setShowModal(false)
      setForm(EMPTY_FORM)
      load()
    } catch (e: any) {
      setError(e.response?.data?.error || 'Error al crear la empresa')
    } finally {
      setSaving(false)
    }
  }

  const filtered = tenants.filter(t =>
    t.name.toLowerCase().includes(search.toLowerCase()) || t.email.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <>
      <div className="page-header">
        <div>
          <div className="page-title">Empresas</div>
          <div className="page-subtitle">{tenants.length} empresa(s) registrada(s)</div>
        </div>
        <button className="btn btn-primary" onClick={() => { setShowModal(true); setError('') }}>+ Nueva empresa</button>
      </div>

      <div className="page-body">
        <div className="card mb-4">
          <input className="form-control" placeholder="Buscar empresa..." value={search} onChange={e => setSearch(e.target.value)} style={{ maxWidth: 300 }} />
        </div>

        {loading ? <div className="loading">Cargando...</div> : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Empresa</th><th>Email</th><th>Tax ID</th><th>Plan</th><th>Usuarios</th><th>Almacenes</th><th>Estado</th><th></th></tr></thead>
              <tbody>
                {filtered.map(t => (
                  <tr key={t.id}>
                    <td><strong>{t.name}</strong><br /><code style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{t.slug}</code></td>
                    <td>{t.email}</td>
                    <td>{t.tax_id || '—'}</td>
                    <td><span className={`badge ${t.plan === 'premium' ? 'badge-warning' : 'badge-info'}`}>{t.plan}</span></td>
                    <td>{t.active_users}</td>
                    <td>{t.active_warehouses}</td>
                    <td><span className={`badge ${STATUS_BADGE[t.status] || 'badge-neutral'}`}>{t.status}</span></td>
                    <td><button className="btn btn-secondary btn-sm" onClick={() => navigate(`/empresas/${t.id}`)}>Ver</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h3 className="modal-title">Nueva empresa</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>×</button>
            </div>
            <div className="modal-body">
              {error && <div className="alert alert-error mb-4">{error}</div>}

              <p className="text-sm text-secondary mb-4" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Datos de la empresa</p>

              <div className="form-group">
                <label className="form-label">Nombre de la empresa *</label>
                <input className="form-control" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Empresa Ejemplo S.A." />
              </div>
              <div className="form-group">
                <label className="form-label">Slug (identificador único)</label>
                <input className="form-control" value={form.slug} onChange={e => set('slug', e.target.value)} placeholder="empresa-ejemplo" />
              </div>
              <div className="form-group">
                <label className="form-label">Email de la empresa *</label>
                <input className="form-control" type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="contacto@empresa.com" />
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Teléfono</label>
                  <input className="form-control" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+56 9 1234 5678" />
                </div>
                <div className="form-group">
                  <label className="form-label">RUT / Tax ID</label>
                  <input className="form-control" value={form.taxId} onChange={e => set('taxId', e.target.value)} placeholder="76.123.456-7" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Dirección</label>
                <input className="form-control" value={form.address} onChange={e => set('address', e.target.value)} placeholder="Av. Principal 123, Santiago" />
              </div>
              <div className="form-group">
                <label className="form-label">Plan</label>
                <select className="form-control" value={form.plan} onChange={e => set('plan', e.target.value)}>
                  <option value="standard">Standard</option>
                  <option value="premium">Premium</option>
                </select>
              </div>

              <p className="text-sm text-secondary mb-4" style={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 16 }}>Administrador de la empresa</p>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Nombre *</label>
                  <input className="form-control" value={form.adminFirstName} onChange={e => set('adminFirstName', e.target.value)} placeholder="Juan" />
                </div>
                <div className="form-group">
                  <label className="form-label">Apellido</label>
                  <input className="form-control" value={form.adminLastName} onChange={e => set('adminLastName', e.target.value)} placeholder="Pérez" />
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Email del administrador *</label>
                <input className="form-control" type="email" value={form.adminEmail} onChange={e => set('adminEmail', e.target.value)} placeholder="admin@empresa.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Contraseña inicial *</label>
                <input className="form-control" type="password" value={form.adminPassword} onChange={e => set('adminPassword', e.target.value)} placeholder="Mínimo 8 caracteres" />
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancelar</button>
              <button className="btn btn-primary" onClick={handleCreate} disabled={saving}>{saving ? 'Creando...' : 'Crear empresa'}</button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
